import {
  BASE_COLORS,
  Input,
  StyledModal,
} from "@bikdotai/bik-component-library";
import React from "react";
import styled from "styled-components";
import EditIcon from "../../../../src/icons/editIcon";
import DeleteIcon from "../../../../src/icons/DeleteIcon";
import { RangeStatic } from "quill";
import { InboxIconContainer } from "./emailEditor.styled";
import { Type } from "@bikdotai/bik-component-library/dist/esm/components/button/model";

interface InsertLinkModalProps {
  isInsertLinkModalOpen: boolean;
  onInsertLinkModalClose: () => void;
  onPrimaryButtonClick: (textValue: string, linkValue: string) => void;
  onSecondaryButtonClick: () => void;
  textValue: string;
  linkValue: string;
  onChangeText: (val: string) => void;
  onChangeLink: (val: string) => void;
}

interface EditLinkModalProps {
  link: string;
  text: string;
  range?: RangeStatic;
  isEditLinkModalOpen: boolean;
  onEditLinkModalClose: () => void;
  onEditLinkButtonClick: () => void;
  onDeleteLinkButtonClick: () => void;
}

const InsertLinkContent = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  margin-left: 8px;
  // to remove the blue border on autocomplete
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0px 1000px #ffffff inset !important;
  }
`;

const EditLinkModalContainer = styled.div`
  padding: 8px 8px;
  display: flex;
  flex-direction: row;
  margin-left: 8px;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  a {
    color: ${BASE_COLORS.brand[500]} !important;
    text-decoration: none !important;
  }
  // to remove the blue border on autocomplete
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0px 1000px #ffffff inset !important;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const InsertLinkModal: React.FC<InsertLinkModalProps> = (
  props: InsertLinkModalProps
) => {
  const {
    isInsertLinkModalOpen,
    onInsertLinkModalClose,
    onPrimaryButtonClick,
    onSecondaryButtonClick,
    textValue,
    linkValue,
    onChangeText,
    onChangeLink,
  } = props;

  const [isError, setIsError] = React.useState(false);

  const checkForErrors = () => {
    // check if the link is valid [ starts with http or https and has a domain name, e.g. google.com ( basically, should start with http:// or https:// and contain at least one '.' in the URL)]
    const isErrorPresent =
      linkValue && !linkValue.match(/^https?:\/\/[^\/\s]+\.[^\/\s]+/)
        ? true
        : false;
    setIsError(isErrorPresent);
    return isErrorPresent;
  };

  const resetError = () => {
    setIsError(false);
  };

  const args = {
    headingTitle: "Insert Link",
    primaryButton: {
      buttonType: "primary" as Type,
      buttonText: "Done",
      disabled: !textValue || !linkValue,
      onClick: () => {
        const isErrorPresent = checkForErrors();
        if (!isErrorPresent) onPrimaryButtonClick(textValue, linkValue);
      },
    },
    secondaryButton: {
      buttonType: "tertiaryGray" as Type,
      buttonText: "Cancel",
      disabled: false,
      onClick: onSecondaryButtonClick,
    },
    footerLeftCustomElement: (
      <button
        style={{
          color: !linkValue
            ? BASE_COLORS.grayscale[400]
            : BASE_COLORS.brand[500],
          fontWeight: "bold",
          fontSize: "14px",
          lineHeight: "20px",
          cursor: linkValue ? "pointer" : "not-allowed",
          background: "none",
          border: "none",
          padding: 0,
        }}
        disabled={!linkValue}
        onClick={() => window.open(linkValue, "_blank")}
      >
        Test this link
      </button>
    ),
  };

  return (
    <StyledModal
      {...args}
      open={isInsertLinkModalOpen}
      onClose={() => {
        resetError();
        onInsertLinkModalClose();
      }}
      closeOnOutsideClick={true}
    >
      <InsertLinkContent>
        <span
          style={{
            color: "#212121",
            fontWeight: "400",
            fontSize: "14px",
            lineHeight: "20px",
            marginBottom: "8px",
          }}
        >
          Text to display
        </span>
        <Input
          id="textToDisplay"
          type="text"
          value={textValue}
          onChangeText={(val) => onChangeText(val)}
          noKeyDownChange={true}
          placeholder="Enter the text"
        />
        <span
          style={{
            color: isError ? "#B92321" : "#212121",
            fontWeight: "400",
            fontSize: "14px",
            lineHeight: "20px",
            marginTop: "20px",
            marginBottom: "8px",
          }}
        >
          To which URL should this link refer?
        </span>
        <Input
          id="linkToRefer"
          type="text"
          value={linkValue}
          onChangeText={(val) => {
            resetError();
            onChangeLink(val);
          }}
          noKeyDownChange={true}
          placeholder="Enter the link"
          errorMessage={isError ? "Enter a valid link" : ""}
        />
      </InsertLinkContent>
    </StyledModal>
  );
};

export const EditLinkModal: React.FC<EditLinkModalProps> = (
  props: EditLinkModalProps
) => {
  const {
    isEditLinkModalOpen,
    onEditLinkModalClose,
    onDeleteLinkButtonClick,
    onEditLinkButtonClick,
    link,
    text,
    range,
  } = props;

  // if the link is very long, we want to show it like first 20 characters and last 20 characters and in between we want to show '...'
  const linkToShow =
    link.length > 40
      ? `${link.substring(0, 20)}...${link.substring(link.length - 20)}`
      : link;

  return (
    <StyledModal
      open={isEditLinkModalOpen}
      hideCrossButton={true}
      centralContainerStyles={{ borderRadius: 4 }}
      width="520px"
      closeOnOutsideClick={true}
      onClose={onEditLinkModalClose}
    >
      <EditLinkModalContainer>
        <span>
          Go to link:{" "}
          <a href={link} target="_blank" rel="noreferrer">
            {linkToShow}
          </a>
        </span>
        <ButtonsContainer>
          <InboxIconContainer onClick={onEditLinkButtonClick}>
            <EditIcon
              width={20}
              height={20}
              color="#212121"
              cursor={"pointer"}
            />
          </InboxIconContainer>
          <div
            style={{
              border: "0.5px solid #000",
              height: 20,
              marginRight: 8,
              marginLeft: 8,
            }}
          ></div>
          <InboxIconContainer onClick={onDeleteLinkButtonClick}>
            <DeleteIcon
              width={20}
              height={20}
              color="#212121"
              cursor={"pointer"}
            />
          </InboxIconContainer>
        </ButtonsContainer>
      </EditLinkModalContainer>
    </StyledModal>
  );
};

export default InsertLinkModal;
